import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import { Pencil, Trash2 } from 'lucide-react'
import { observer } from 'mobx-react'
import { useContext, useRef, useState } from 'react'

import { ConfirmButton } from '@/components/ConfirmButton'
import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { Button } from '@/components/elements/button'
import { Input } from '@/components/elements/input'
import { Layout } from '@/components/Layout'
import {
  deleteTraining as deleteTrainingService,
  getTrainings,
} from '@/services/trainings.service'
import { UserStoreContext } from '@/stores/UserStore'
import { cn, toast } from '@/utils/helpers'

import AddEditTrainingModal from './AddEditTrainingModal'

const Trainings = observer(() => {
  // Context
  const { isXverseAdmin } = useContext(UserStoreContext)

  // State
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [sorting, setSorting] = useState([{ id: 'name', desc: false }])
  const [pagination, setPagination] = useState({
    pageSize: 20,
    pageIndex: 0,
  })
  const [showTrainingModal, setShowTrainingModal] = useState(false)
  const [editTraining, setEditTraining] = useState(null)

  // Data Processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const { data: trainings, isPending } = useQuery({
    queryKey: ['trainings', sorting, pagination, searchTerm],
    queryFn: () => getTrainings({ sorting, pagination, searchTerm }),
    placeholderData: keepPreviousData,
  })
  const { isPending: pendingDelete, mutate: deleteTraining } = useMutation({
    mutationFn: (data) => deleteTrainingService(data.id),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess('Training deleted successfully')

      // Refresh trainings list
      queryClient.invalidateQueries({ queryKey: ['trainings'] })
    },
  })

  // Search shrinks the result set, so a deep page can 404 from the paginator.
  // Reset to page 0 in the same handler so React batches the state updates.
  const resetToFirstPage = () =>
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }))

  const debounceSearch = useRef(
    _.debounce((value) => {
      setSearchTerm(value)
      resetToFirstPage()
    }, 500),
  ).current

  return (
    <Layout breadcrumbs={['Admin', 'Trainings']}>
      <Layout.Heading title="Trainings" />

      {/* @fixme: This is a hacky way to make the table filters responsive */}
      <div className="flex flex-row items-center gap-2">
        {searchTerm && (
          <Button
            className="visible sm:hidden"
            variant="outline"
            onClick={() => {
              setSearch('')
              setSearchTerm('')
              resetToFirstPage()
            }}
          >
            Clear Filters
          </Button>
        )}
        <Button
          className="visible sm:hidden"
          disabled={!isXverseAdmin}
          onClick={() => setShowTrainingModal(true)}
        >
          Add Training
        </Button>
      </div>

      <div className="flex w-full flex-row items-center justify-between">
        <div className="mt-2 mb-2 flex w-full flex-col items-center gap-4 py-1 sm:flex-row">
          <Input
            className="w-full sm:w-64"
            placeholder="Search for Training"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              debounceSearch(e.target.value)
            }}
          />

          {searchTerm && (
            <Button
              className="hidden sm:block"
              variant="outline"
              onClick={() => {
                setSearch('')
                setSearchTerm('')
                resetToFirstPage()
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        <Button
          className="hidden sm:block"
          disabled={!isXverseAdmin}
          onClick={() => setShowTrainingModal(true)}
        >
          Add Training
        </Button>
      </div>

      <DataTable
        columns={[
          {
            accessorKey: 'name',
            header: ({ column }) => <SortableColumnHeader column={column} label="Name" />,
            cell: ({ row }) => (
              <button
                type="button"
                className="text-action cursor-pointer text-left hover:underline"
                disabled={!isXverseAdmin}
                onClick={() => {
                  setEditTraining(row.original)
                  setShowTrainingModal(true)
                }}
              >
                {row.original.name}
              </button>
            ),
          },
          {
            accessorKey: 'description',
            header: ({ column }) => <SortableColumnHeader column={column} label="Description" />,
            cell: ({ cell }) => (
              <span
                className={cn(
                  'max-w-[200px] truncate text-ellipsis',
                  !cell.getValue() && 'text-muted-foreground',
                )}
              >
                {cell.getValue() || 'No description provided'}
              </span>
            ),
          },
          {
            id: 'edit',
            cell: ({ row }) => (
              <Button
                className="place-self-end"
                disabled={!isXverseAdmin}
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditTraining(row.original)
                  setShowTrainingModal(true)
                }}
              >
                <Pencil className="mr-1 h-4 w-4" />
                Edit
              </Button>
            ),
            size: 80,
          },
          {
            id: 'delete',
            cell: ({ row }) => (
              <ConfirmButton
                confirmContent={`Are you sure you want to delete the training: ${row.original.name}?`}
                confirmTitle="Delete Training"
                className="place-self-end"
                disabled={!isXverseAdmin}
                variant="destructive"
                size="sm"
                loading={pendingDelete}
                onClick={() => deleteTraining(row.original)}
              >
                <Trash2 className="h-4 w-4" />
                <span className="sr-only">Delete training</span>
              </ConfirmButton>
            ),
            size: 80,
          },
        ]}
        data={trainings?.results || []}
        loading={isPending}
        pagination={pagination}
        rowCount={trainings?.count || 0}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
      />

      {showTrainingModal && (
        <AddEditTrainingModal
          editTraining={editTraining}
          resetAndClose={() => {
            setShowTrainingModal(false)
            setEditTraining(null)
            queryClient.invalidateQueries({ queryKey: ['trainings'] })
          }}
        />
      )}
    </Layout>
  )
})

export default Trainings
